/**
 * Models API Hooks
 *
 * TanStack Query hooks for trained models.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listModelsApiV1ModelsGet,
  getModelApiV1ModelsModelIdGet,
  updateModelApiV1ModelsModelIdPatch,
  deleteModelApiV1ModelsModelIdDelete,
} from '@/api';
import type { ModelUpdate } from '@/api';
import { useAuthEnabled } from './useAuthQuery';

// Query keys factory
export const modelKeys = {
  all: ['models'] as const,
  lists: () => [...modelKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...modelKeys.lists(), filters] as const,
  details: () => [...modelKeys.all, 'detail'] as const,
  detail: (id: string) => [...modelKeys.details(), id] as const,
};

/**
 * List all models.
 */
export function useModels(options?: {
  filters?: { is_active?: boolean | null };
  enabled?: boolean;
}) {
  const { enabled: authEnabled } = useAuthEnabled(options?.enabled ?? true);

  return useQuery({
    queryKey: modelKeys.list(options?.filters),
    queryFn: async () => {
      const response = await listModelsApiV1ModelsGet({
        query: options?.filters,
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch models'
        );
      }
      return response.data?.items ?? [];
    },
    enabled: authEnabled,
  });
}

/**
 * Get a single model.
 */
export function useModel(modelId: string) {
  const { enabled: authEnabled } = useAuthEnabled(Boolean(modelId));

  return useQuery({
    queryKey: modelKeys.detail(modelId),
    queryFn: async () => {
      const response = await getModelApiV1ModelsModelIdGet({
        path: { model_id: modelId },
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch model'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * Update a model (name, description).
 */
export function useUpdateModel(modelId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ModelUpdate) => {
      const response = await updateModelApiV1ModelsModelIdPatch({
        path: { model_id: modelId },
        body: data,
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to update model'
        );
      }
      return response.data;
    },
    onSuccess: (updatedModel) => {
      queryClient.setQueryData(modelKeys.detail(modelId), updatedModel);
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
    },
  });
}

/**
 * Archive a model (soft delete).
 */
export function useArchiveModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (modelId: string) => {
      const response = await deleteModelApiV1ModelsModelIdDelete({
        path: { model_id: modelId },
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to archive model'
        );
      }
      return response.data;
    },
    onSuccess: (_, modelId) => {
      queryClient.removeQueries({ queryKey: modelKeys.detail(modelId) });
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
    },
  });
}
